import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Mock user mapping to ensure login connects to DataContext mock data
const MOCK_DB = {
  // Candidates matching DataContext IDs
  'john@example.com': { id: '123', name: 'John Doe', role: 'candidate', profileComplete: true, city: 'New York' },
  'jane@example.com': { id: '124', name: 'Jane Smith', role: 'candidate', profileComplete: true, city: 'San Francisco' },
  'mike@example.com': { id: '125', name: 'Mike Johnson', role: 'candidate', profileComplete: true, city: 'Chicago' },
  
  // Companies matching DataContext IDs
  'tech@techcorp.com': { id: '1', name: 'TechCorp Inc.', role: 'company', profileComplete: true, industry: 'Technology' },
  'hr@datasoft.com': { id: '2', name: 'DataSoft Solutions', role: 'company', profileComplete: true, industry: 'Data Analytics' },
  
  // Hosts matching DataContext IDs
  'admin@techuni.edu': { id: 'host1', name: 'Tech University', role: 'host', type: 'University' },
  'contact@cityhub.org': { id: 'host2', name: 'City Innovation Hub', role: 'host', type: 'Organization' },
  
  // Super Admin
  'admin@jobfair.com': { id: 'admin1', name: 'System Administrator', role: 'super_admin' }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('jobfair_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const login = async (email, password, role) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));

      let userData;

      // Check if this is a pre-defined mock user to link with existing data
      // We normalize email to lowercase for checking
      const normalizedEmail = email.toLowerCase();
      
      if (MOCK_DB[normalizedEmail] && MOCK_DB[normalizedEmail].role === role) {
        userData = { 
          ...MOCK_DB[normalizedEmail], 
          email: normalizedEmail 
        };
      } else {
        // If not a pre-defined user, create a new session
        // Note: These users won't have pre-filled data in the dashboard
        userData = {
          id: Date.now().toString(),
          email: normalizedEmail,
          role,
          name: email.split('@')[0],
          profileComplete: false
        };
      }

      setUser(userData);
      localStorage.setItem('jobfair_user', JSON.stringify(userData));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newUser = {
        ...userData,
        id: Date.now().toString(),
        profileComplete: false
      };
      
      setUser(newUser);
      localStorage.setItem('jobfair_user', JSON.stringify(newUser));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('jobfair_user');
  };

  const updateProfile = (profileData) => {
    const updatedUser = { ...user, ...profileData, profileComplete: true };
    setUser(updatedUser);
    localStorage.setItem('jobfair_user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};